import { env } from '@/lib/env'
import { haversineMetres, type LatLng } from './geo'

/**
 * Road routing for the line the map draws between pickup and drop-off.
 *
 * Talks to OSRM, which is a public service and not the Drive For Me API — so it
 * deliberately does not go through `api-client`: no bearer token belongs on a
 * third-party host, and a 500 from a demo router must not surface as "the
 * server had a problem" on an admin screen.
 */

/** Road routing gave up: this is the direct line between the two points. */
export type RouteKind = 'road' | 'straight'

export interface DrawnRoute {
  /** The line to draw, pickup first, in map order. */
  path: LatLng[]
  distanceMetres: number
  /** Driving time in seconds, or null when there is no road answer to take it from. */
  durationSeconds: number | null
  kind: RouteKind
}

/** Long enough for a cold OSRM demo server, short enough not to strand the dialog. */
const ROUTE_TIMEOUT_MS = 8_000

interface OsrmRoute {
  distance?: number
  duration?: number
  geometry?: { coordinates?: [number, number][] }
}

interface OsrmResponse {
  code?: string
  routes?: OsrmRoute[]
}

/**
 * The answer when routing is unavailable: the crow-flies line.
 *
 * A straight line across Cairo is wrong about the roads but right about where
 * the journey starts and ends, which is what an operator opened the map for. It
 * is drawn dashed and labelled, so nobody reads it as a driving route.
 */
export function straightLineRoute(from: LatLng, to: LatLng): DrawnRoute {
  return {
    path: [from, to],
    distanceMetres: haversineMetres(from, to),
    durationSeconds: null,
    kind: 'straight',
  }
}

/**
 * Driving geometry from OSRM, or the straight line if anything goes wrong.
 *
 * Never rejects on a routing failure — an unreachable router, a 429, a
 * malformed body and a route that does not exist all resolve to the fallback,
 * because a map that draws something and admits what it is beats an error
 * panel. The only rejection is the caller's own cancellation, which React Query
 * needs to see to discard the result.
 */
export async function fetchDrivenRoute(
  from: LatLng,
  to: LatLng,
  signal?: AbortSignal,
): Promise<DrawnRoute> {
  const controller = new AbortController()
  const abortFromCaller = () => controller.abort()
  const timer = setTimeout(() => controller.abort(), ROUTE_TIMEOUT_MS)
  signal?.addEventListener('abort', abortFromCaller)

  try {
    // OSRM takes longitude first — the opposite of every other coordinate in
    // this codebase. Reversing these silently routes between two points in the
    // Arabian Sea and still answers 200.
    const coordinates = `${from.lng},${from.lat};${to.lng},${to.lat}`
    const url = `${env.osrmBaseUrl}/route/v1/driving/${coordinates}?overview=full&geometries=geojson`

    const response = await fetch(url, { signal: controller.signal })
    if (!response.ok) return straightLineRoute(from, to)

    const body = (await response.json()) as OsrmResponse
    const route = body.code === 'Ok' ? body.routes?.[0] : undefined
    const geometry = route?.geometry?.coordinates

    if (!route || !Array.isArray(geometry) || geometry.length < 2) {
      return straightLineRoute(from, to)
    }

    return {
      // GeoJSON is [longitude, latitude]; Leaflet is the other way round.
      path: geometry.map(([lng, lat]) => ({ lat, lng })),
      distanceMetres: typeof route.distance === 'number' ? route.distance : haversineMetres(from, to),
      durationSeconds: typeof route.duration === 'number' ? route.duration : null,
      kind: 'road',
    }
  } catch (error) {
    // The dialog was closed or the query superseded: let React Query see it.
    if (signal?.aborted) throw error
    return straightLineRoute(from, to)
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', abortFromCaller)
  }
}
